import React, { useEffect, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { UseOutletContextUser } from './User';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAuthUser } from '../../../services/auth/user.service';
import { ApiDigitalAuthAuthentication } from '../../../services/auth/authentication.service';
import { Error } from '../../../models/error.model';
import { Message } from '../../../models/message.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { Container } from '../../../styles/container.style';
import { legendIconInvalid, legendIconValidInvalidRestart } from '../../../utils/legend.data.util';
import { uploadTooltip } from '../../../utils/tooltip.util';
import { evaluateLegendRequiredIcon } from '../../../scripts/validate.legend.script';
import { evaluatePassword, evaluatePasswordConfirmation } from '../../../scripts/validate.password.script';
import { expressions } from '../../../libraries/regular.expressions.library';
import { getError, getMessage } from '../../../languages/translations/response';
import { uploadUrl } from '../../../scripts/file.load.script';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { authClear } from '../../../scripts/authentication.script';

import AppLegend from '../../../components/element/Legend';
import AppLoading from '../../../components/Loading';
import AppInputImage from '../../../components/element/InputImage';

export interface UserUpdatePageProps {};

let errorResponse: Error, messageResponse: Message;

const UserUpdatePage: React.FunctionComponent<UserUpdatePageProps> = props => {
  const {setRoute, appServiceForUser, user, loadUser} = UseOutletContextUser()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState({user_details: 'off', user_email: 'off', user_password: 'off'})
  const [collapse, setCollapse] = useState({user_details: true, sign_method: true, security: true})
  const [container, setContainer] = useState({email: false, password: false})
  const [avatar, setAvatar] = useState({value: {name: '', size: 0, type: '', view: '', file: null as any}, valid: {file: true, message: ''}})
  const [firstName, setFirstName] = useState({value: '', valid: false})
  const [lastName, setLastName] = useState({value: '', valid: false})
  const [phone, setPhone] = useState({value: '', valid: false})
  const [email, setEmail] = useState({value: '', valid: false})
  const [emailPassword, setEmailPassword] = useState({value: '', valid: false})
  const [passwordCurrent, setPasswordCurrent] = useState({value: '', valid: false})
  const [passwordNew, setPasswordNew] = useState({value: '', valid: false})
  const [passwordConfirmation, setPasswordConfirmation] = useState({value: '', valid: false})

  const submitUpdateProfileDetails = async () => {
    setLoadIndicator({...loadIndicator, user_details: 'on'})

    if (appServiceForUser && avatar.valid.file && firstName.valid && lastName.valid && phone.valid) {
      await ApiDigitalAuthUser.putUser(appServiceForUser.api_key, avatar.value.file, firstName.value, lastName.value, phone.value).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data

          loadUser(appServiceForUser.api_key, () => {
            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              restartFormProfileDetails()
            })
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator({...loadIndicator, user_details: 'off'})
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!firstName.value) legendIconInvalid('input-first-name', 'legend-first-name-required')
        if (!lastName.value) legendIconInvalid('input-last-name', 'legend-last-name-required')
        if (!phone.value) legendIconInvalid('input-phone', 'legend-phone-required')
        setLoadIndicator({...loadIndicator, user_details: 'off'})
      })
    }
  }

  const submitUpdateUserEmail = async () => {
    setLoadIndicator({...loadIndicator, user_email: 'on'})

    if (appServiceForUser && email.valid && emailPassword.valid) {
      await ApiDigitalAuthUser.putUserEmail(appServiceForUser.api_key, email.value, emailPassword.value).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data

          loadUser(appServiceForUser.api_key, () => {
            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              setContainer({...container, email: false})
              setEmailPassword({value: '', valid: false})
              restartFormUserEmail()
            })
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator({...loadIndicator, user_email: 'off'})
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!email.value) legendIconInvalid('input-email', 'legend-email-required')
        if (!emailPassword.value) legendIconInvalid('input-email-password', 'legend-email-password-required')
        setLoadIndicator({...loadIndicator, user_email: 'off'})
      })
    }
  }

  const submitUpdateUserPassword = async () => {
    setLoadIndicator({...loadIndicator, user_password: 'on'})

    if (appServiceForUser && passwordCurrent.valid && passwordNew.valid && passwordConfirmation.valid) {
      await ApiDigitalAuthUser.putUserPassword(appServiceForUser.api_key, passwordCurrent.value, passwordNew.value, passwordConfirmation.value).then( (response) => {
        if (response.status === 200) {
          messageResponse = response.data

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            setContainer({...container, password: false})
            setPasswordCurrent({value: '', valid: false})
            setPasswordNew({value: '', valid: false})
            setPasswordConfirmation({value: '', valid: false})
            restartFormUserPassword()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator({...loadIndicator, user_password: 'off'})
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!passwordCurrent.value) legendIconInvalid('input-password-current', 'legend-password-current-required')
        if (!passwordNew.value) legendIconInvalid('input-password-new', 'legend-password-new-required')
        if (!passwordConfirmation.value) legendIconInvalid('input-password-confirmation', 'legend-confirmation-password-required')
        setLoadIndicator({...loadIndicator, user_password: 'off'})
      })
    }
  }

  const submitSignOut = () => {
    if (!appServiceForUser) return null

    GlobalSwal.fire({
      title: lang.labels.youWantLogOut,
      text: lang.labels.willNotBeAbleToReturnUntilYouLogin,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesLogOut,
      cancelButtonText: lang.labels.noCancel
    }).then( async (result) => {
      if (result.isConfirmed) {
        try {
          await ApiDigitalAuthAuthentication.logoutAll(appServiceForUser.api_key)
        } catch {
          // Ignore — tokens may already be expired. We still clean up below.
        }

        authClear()

        GlobalSwal.fire({
          title: lang.labels.redirectingToLogin,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        }).then( () => {
          navigate('/auth/login', {replace: true})
        })
      }
    })
  }

  const handleChangeFirstName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFirstName({...firstName, value: event.target.value})
  }

  const handleChangeLastName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setLastName({...lastName, value: event.target.value})
  }

  const handleChangePhone = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPhone({...phone, value: event.target.value})
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const handleChangeEmailPassword = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmailPassword({ ...emailPassword, value: event.target.value })
  }

  const handleChangePasswordCurrent = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPasswordCurrent({ ...passwordCurrent, value: event.target.value })
  }

  const handleChangePasswordNew = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPasswordNew({ ...passwordNew, value: event.target.value })
  }

  const handleChangePasswordConfirmation = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPasswordConfirmation({...passwordConfirmation , value: event.target.value})
  }

  const validateFirstName = () => {
    evaluateLegendRequiredIcon(expressions.user, firstName, setFirstName, 'input-first-name', 'legend-first-name-valid', 'legend-first-name-required')
  }

  const validateLastName = () => {
    evaluateLegendRequiredIcon(expressions.user, lastName, setLastName, 'input-last-name', 'legend-last-name-valid', 'legend-last-name-required')
  }

  const validatePhone = () => {
    evaluateLegendRequiredIcon(expressions.phone, phone, setPhone, 'input-phone', 'legend-phone-valid', 'legend-phone-required')
  }

  const validateEmail = () => {
    evaluateLegendRequiredIcon(expressions.email, email, setEmail, 'input-email', 'legend-email-valid', 'legend-email-required')
  }

  const validateEmailPassword = () => {
    evaluateLegendRequiredIcon(expressions.limit, emailPassword, setEmailPassword, 'input-email-password', 'legend-email-password-valid', 'legend-email-password-required')
  }

  const validatePasswordCurrent = () => {
    evaluateLegendRequiredIcon(expressions.limit, passwordCurrent, setPasswordCurrent, 'input-password-current', 'legend-password-current-valid', 'legend-password-current-required')
  }

  const validatePasswordNew = () => {
    evaluatePassword(passwordNew, setPasswordNew, 'input-password-new', 'legend-password-new-valid', 'legend-password-new-required')
    evaluatePasswordConfirmation(true, passwordNew, passwordConfirmation, setPasswordConfirmation,'input-password-confirmation', 'legend-confirmation-password-identical', 'legend-confirmation-password-valid', 'legend-confirmation-password-required')
  }

  const validatePasswordConfirmation = () => {
    evaluatePasswordConfirmation(false, passwordNew, passwordConfirmation, setPasswordConfirmation,'input-password-confirmation', 'legend-confirmation-password-identical', 'legend-confirmation-password-valid', 'legend-confirmation-password-required')
  }

  const executeCollapse = (item: string) => {
    switch (item) {
      case 'profile-details':
        setCollapse({...collapse, user_details: !collapse.user_details})
        break;
      case 'sign-in-method':
        setCollapse({...collapse, sign_method: !collapse.sign_method})
        break;
    case 'security':
        setCollapse({...collapse, security: !collapse.security})
        break;
    }
  }

  const executeChangeContainer = (item: string) => {
    switch (item) {
      case 'email':
        setContainer({...container, email: !container.email})
        break;
      case 'password':
        setContainer({...container, password: !container.password})
        break;
    }
  }

  const executeRestartFormProfileDetails = () => {
    uploadData()
    restartFormProfileDetails()
  }

  function restartFormProfileDetails() {
    legendIconValidInvalidRestart('input-first-name', 'legend-first-name-valid')
    legendIconValidInvalidRestart('input-first-name', 'legend-first-name-required')
    legendIconValidInvalidRestart('input-last-name', 'legend-last-name-valid')
    legendIconValidInvalidRestart('input-last-name', 'legend-last-name-required')
    legendIconValidInvalidRestart('input-phone', 'legend-phone-valid')
    legendIconValidInvalidRestart('input-phone', 'legend-phone-required')
  }

  function restartFormUserEmail() {
    legendIconValidInvalidRestart('input-email', 'legend-email-valid')
    legendIconValidInvalidRestart('input-email', 'legend-email-required')
    legendIconValidInvalidRestart('input-email-password', 'legend-email-password-valid')
    legendIconValidInvalidRestart('input-email-password', 'legend-email-password-required')
  }

  function restartFormUserPassword() {
    legendIconValidInvalidRestart('input-password-current', 'legend-password-current-valid')
    legendIconValidInvalidRestart('input-password-current', 'legend-password-current-required')
    legendIconValidInvalidRestart('input-password-new', 'legend-password-new-valid')
    legendIconValidInvalidRestart('input-password-new', 'legend-password-new-required')
    legendIconValidInvalidRestart('input-password-confirmation', 'legend-confirmation-password-identical')
    legendIconValidInvalidRestart('input-password-confirmation', 'legend-confirmation-password-valid')
    legendIconValidInvalidRestart('input-password-confirmation', 'legend-confirmation-password-required')
  }

  function uploadData() {
    if (user) {
      setAvatar(uploadUrl(lang, user.avatar || ''))
      setFirstName({value: user.first_name, valid: true})
      setLastName({value: user.last_name, valid: true})
      setPhone({value: user.phone, valid: true})
      setEmail({value: user.email, valid: true})
    }
  }

  useEffect( () => {
    uploadData()
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.user, branch: lang.labels.updateUser}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <>
      <div className="card mb-5">
        <div className="card-header cursor-pointer" role="button" onClick={() => executeCollapse("profile-details")}>
          <div className="card-title">
            <h4>{lang.labels.profileDetails}</h4>
          </div>
        </div>
        <Collapse in={collapse.user_details}>
          <div className="form">
            <div className="card-body">
              <div className="row mb-5">
                <label className="col-lg-3 col-form-label">{lang.labels.avatar}</label>
                <div className="col-lg-9">
                  <AppInputImage image={avatar} setImage={setAvatar}></AppInputImage>
                </div>
              </div>
              <div className="row mb-5">
                <label className="col-lg-3 col-form-label required">{lang.labels.fullName}</label>
                <div className="col-lg-9">
                  <div className="row">
                    <div className="col-lg-6 fv-row">
                      <input id="input-first-name" className="form-control mb-3 mb-lg-0" type="text" autoComplete="off" name="first-name" placeholder={lang.labels.firstName} value={firstName.value} onChange={handleChangeFirstName} onKeyUp={validateFirstName} onBlur={validateFirstName} />
                      <AppLegend attribute={{check: firstName.valid, name: "first-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className="col-lg-6 fv-row">
                      <input id="input-last-name" className="form-control" type="text" autoComplete="off" name="last-name" placeholder={lang.labels.lastName} value={lastName.value} onChange={handleChangeLastName} onKeyUp={validateLastName} onBlur={validateLastName} />
                      <AppLegend attribute={{check: lastName.valid, name: "last-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <label className="col-lg-3 col-form-label required">{lang.labels.phoneNumber}</label>
                <div className="col-lg-9 fv-row">
                  <input id="input-phone" className="form-control" type="tel" autoComplete="off" name="phone" value={phone.value} onChange={handleChangePhone} onKeyUp={validatePhone} onBlur={validatePhone} />
                  <AppLegend attribute={{check: phone.valid, name: "phone", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
            </div>
            <div className="card-footer d-flex justify-content-end">
              <button className="btn btn-secondary" type="reset" onClick={executeRestartFormProfileDetails}>{lang.labels.discard}</button>
              <button className="btn btn-primary ms-2" type="button" data-kt-indicator={loadIndicator.user_details} onClick={submitUpdateProfileDetails}>
                <span className="indicator-label">{lang.labels.saveChanges}</span>
                <span className="indicator-progress">
                  {lang.labels.pleaseWait}
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>
            </div>
          </div>
        </Collapse>
      </div>
      <div className="card mb-5">
        <div className="card-header cursor-pointer" role="button" onClick={() => executeCollapse("sign-in-method")}>
          <div className="card-title">
            <h4>{lang.labels.signInMethod}</h4>
          </div>
        </div>
        { user
          ?
          <Collapse in={collapse.sign_method}>
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center">
                <Container property={(!container.email).toString()}>
                  <div className="fs-6 fw-bolder mb-1">{lang.labels.emailAddress}</div>
                  <div className="fw-bold text-muted">{user.email}</div>
                </Container>
                <Container property={(container.email).toString()} className="flex-row-fluid">
                  <div className="form">
                    <div className="row mb-5">
                      <div className="col-lg-6 mb-5 mb-lg-0">
                        <div className="fv-row">
                          <label className="form-label">{lang.labels.enterNewEmailAddress}</label>
                          <input id="input-email" className="form-control" type="email" autoComplete="off" name="email" value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
                          <AppLegend attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="fv-row">
                          <label className="form-label">{lang.labels.confirmPassword}</label>
                          <input id="input-email-password" className="form-control" type="password" autoComplete="off" name="emailPassword" value={emailPassword.value} onChange={handleChangeEmailPassword} onKeyUp={validateEmailPassword} onBlur={validateEmailPassword} />
                          <AppLegend attribute={{check: emailPassword.valid, name: "email-password", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex">
                      <button className="btn btn-primary me-2" type="button" data-kt-indicator={loadIndicator.user_email} onClick={submitUpdateUserEmail}>
                        <span className="indicator-label">{lang.labels.updateEmail}</span>
                        <span className="indicator-progress">
                          {lang.labels.pleaseWait}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      </button>
                      <button className="btn btn-secondary" type="reset" onClick={() => executeChangeContainer("email")}>{lang.labels.cancel}</button>
                    </div>
                  </div>
                </Container>
                <Container property={(!container.email).toString()} className="ms-auto">
                  <button className="btn btn-light-primary" type="button" onClick={() => executeChangeContainer("email")}>{lang.labels.changeEmail}</button>
                </Container>
              </div>
              <div className="separator separator-dashed my-5"></div>
              <div className="d-flex flex-wrap align-items-center">
                <Container property={(!container.password).toString()}>
                  <div className="fs-6 fw-bolder mb-1">{lang.labels.password}</div>
                  <div className="fw-bold text-muted">**********************</div>
                </Container>
                <Container property={(container.password).toString()} className="flex-row-fluid">
                  <div className="form">
                    <div className="row">
                      <div className="col-lg-4">
                        <div className="fv-row">
                          <label className="form-label">{lang.labels.currentPassword}</label>
                          <input id="input-password-current" className="form-control" type="password" autoComplete="off" name="password-current" value={passwordCurrent.value} onChange={handleChangePasswordCurrent} onKeyUp={validatePasswordCurrent} onBlur={validatePasswordCurrent} />
                          <AppLegend attribute={{check: passwordCurrent.valid, name: "password-current", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                        </div>
                      </div>
                      <div className="col-lg-4">
                        <div className="fv-row">
                          <label className="form-label">{lang.labels.newPassword}</label>
                          <input id="input-password-new" className="form-control" type="password" autoComplete="off" name="password-new" value={passwordNew.value} onChange={handleChangePasswordNew} onKeyUp={validatePasswordNew} onBlur={validatePasswordNew} />
                          <AppLegend attribute={{check: passwordNew.valid, name: "password-new", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                        </div>
                      </div>
                      <div className="col-lg-4">
                        <div className="fv-row">
                          <label className="form-label">{lang.labels.confirmNewPassword}</label>
                          <input id="input-password-confirmation" className="form-control" type="password" autoComplete="off" name="password-confirmation" value={passwordConfirmation.value} onChange={handleChangePasswordConfirmation} onKeyUp={validatePasswordConfirmation} onBlur={validatePasswordConfirmation} />
                          <AppLegend attribute={{check: passwordConfirmation.valid, name: "confirmation-password", index: null, sub_index: null}} container={{valid: true, required: true, identical: true, exists: false}}></AppLegend>
                        </div>
                      </div>
                    </div>
                    <div className="form-text text-justify mb-5">{lang.labels.use8OrMoreCharacters}</div>
                    <div className="d-flex">
                      <button className="btn btn-primary me-2" type="button" data-kt-indicator={loadIndicator.user_password} onClick={submitUpdateUserPassword}>
                        <span className="indicator-label">{lang.labels.updatePassword}</span>
                        <span className="indicator-progress">
                          {lang.labels.pleaseWait}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      </button>
                      <button className="btn btn-secondary" type="reset" onClick={() => executeChangeContainer("password")}>{lang.labels.cancel}</button>
                    </div>
                  </div>
                </Container>
                <Container property={(!container.password).toString()} className="ms-auto">
                  <button className="btn btn-light-primary" type="button" onClick={() => executeChangeContainer("password")}>{lang.labels.changePassword}</button>
                </Container>
              </div>
            </div>
          </Collapse>
          :
          <AppLoading></AppLoading>
        }
      </div>
      <div className="card">
        <div className="card-header cursor-pointer" role="button" onClick={() => executeCollapse("security")}>
          <div className="card-title">
            <h4>{lang.labels.security}</h4>
          </div>
        </div>
        { user
          ?
          <Collapse in={collapse.security}>
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center">
                <div>
                  <div className="fs-6 fw-bolder">{lang.labels.signOutAllDevices}</div>
                </div>
                <div className="ms-auto">
                  <button className="btn btn-light-primary" type="button" onClick={submitSignOut}>{lang.labels.signOut}</button>
                </div>
              </div>
            </div>
          </Collapse>
          :
          <AppLoading></AppLoading>
        }
      </div>
    </>
  )
};

export default UserUpdatePage;
